import { Inject, Injectable, Optional } from '@angular/core';
import * as amplitude from '@amplitude/analytics-browser';
import { PluginEventDataPropertyNames, UserEventDataTypeMapping, UserEvents } from '@anecdotes/user-event-data-access';
import { AuthService, IS_EXTERNAL_PAGE_TOKEN, UserClaims } from '@anecdotes/auth-core';
import { UserEventService } from '../user-event/user-event.service';
import { filter, map, mergeMap } from 'rxjs/operators';
import { AppConfigService } from '@anecdotes/configuration-data-access';
import { combineLatest, firstValueFrom, of } from 'rxjs';
import { AbstractUserEventConfig } from '../abstract-user-event-config/abstract-user-event-config.service';
import { AccountTypeEnum } from '@anecdotes/api-contracts';
import { User } from '@angular/fire/auth';

export interface AmplitudeUserPropertiesEntity {
  /**
   * User account email
   */
  ['Email']: string;

  /**
   * First name + Last name
   */
  ['User name']: string;

  /**
   * Role
   */
  ['User type']: string;

  /**
   * Name of the customer organization
   */

  ['Account name']: string;

  /**
   * Organization code identifier
   */

  ['Account ID']: string;

  /**
   * Buckets of organization size
   */

  ['Account seat size']: string;

  /**
   * The Account type of the customer
   */
  ['Account type']: string;
}

@Injectable()
export class AmplitudeService {
  private areUserPropertiesSet: boolean;

  constructor(
    private configService: AppConfigService,
    private authService: AuthService,
    private userEventService: UserEventService,
    private userEventConfig: AbstractUserEventConfig,
    @Optional() @Inject(IS_EXTERNAL_PAGE_TOKEN) private isExternalPage: boolean,
  ) {}

  async init(): Promise<any> {
    if (this.configService.config.amplitude) {
      const customer = !this.isExternalPage
        ? await firstValueFrom(this.userEventConfig.getCurrentCustomerInfo())
        : null;
      this.createAmplitudeInstance(customer?.account_type === AccountTypeEnum.InternalTest);
      this.trackEvents();
    }
  }

  private trackEvents(): void {
    /* Global subscription */
    this.userEventService
      .subscribeForAllEvents()
      .pipe(
        mergeMap((event) =>
          combineLatest([of(event), this.isExternalPage ? of(true) : this.authService.isAuthenticatedAsync()]),
        ),
        filter(([, isAuthenticated]) => isAuthenticated),
        map(([event]) => event),
      )
      .subscribe(async (event) => {
        const serializedEventData = this.serializeSpecificEventData(event.eventType, event.eventData);
        let user: UserClaims;
        const authState: User = await firstValueFrom(this.authService.getAuthState());

        if (!this.isExternalPage) {
          user = await this.authService.getUserAsync();
        // Only for external page
        } else if (authState) {
          user = { email: authState.email, name: authState.displayName };
        }

        if (user && !this.areUserPropertiesSet) {
          const identify = new amplitude.Identify();

          if (!this.isExternalPage) {
            const customer = await firstValueFrom(this.userEventConfig.getCurrentCustomerInfo());
            identify
              .set('Email', user.email)
              .set('User name', user.name)
              .set('User type', user.role)
              .set('Account type', customer.account_type)
              .set('Account name', customer.customer_name);
          } else {
            identify
              .set('Email', user.email)
              .set('User name', user.name);
          }

          amplitude.identify(identify);
          this.areUserPropertiesSet = true;
        }

        amplitude.track(event.eventType, serializedEventData);
      });
  }

  serializeSpecificEventData(eventType: UserEvents, eventData: any): any {
    switch (eventType) {
      case UserEvents.PLUGINS_INNER_TAB_NAVIGATION: {
        const eventTypedData = eventData as UserEventDataTypeMapping[typeof eventType];
        return {
          ...eventTypedData,
          [PluginEventDataPropertyNames.PluginCategory]:
            eventTypedData[PluginEventDataPropertyNames.PluginCategory].join(', '),
        };
      }
      default:
        return eventData;
    }
  }

  private createAmplitudeInstance(disableSendingEvents = false): void {
    amplitude.init(this.configService.config.amplitude.apiKey, null, {
      optOut: disableSendingEvents,
      defaultTracking: false,
    });
  }
}
